import React, { useState, useMemo } from 'react';
import api from '../services/api';
import './BulkAnalysisModule.css';
const MAX_FILES = 50;

function b64ToBlob(b64, mime) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function BulkAnalysisModule() {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [generateReports, setGenerateReports] = useState(true);
  const [reportFmt, setReportFmt] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = React.useRef();

  const handleFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles((prev) => {
      const combined = [...prev, ...incoming];
      const seen = new Set();
      const unique = [];
      for (const f of combined) {
        const k = `${f.name}_${f.size}`;
        if (!seen.has(k)) { seen.add(k); unique.push(f); }
      }
      return unique.slice(0, MAX_FILES);
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const clearAll = () => {
    setFiles([]);
    setResults(null);
    setSummary(null);
    setError(null);
  };

  const runBulk = async () => {
    if (files.length === 0) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setSummary(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      fd.append('generate_reports', String(generateReports));
      fd.append('report_fmt', reportFmt);
      const resp = await api.post('/bulk-analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
      });
      setResults(resp.data.results || []);
      setSummary(resp.data.summary || null);
    } catch (err) {
      console.error('Bulk analyze failed', err);
      setError(err?.response?.data?.detail || err.message || 'Bulk analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadEmbedded = (item) => {
    if (!item.report_b64) return;
    const fmt = item.report_fmt || reportFmt;
    const mime = fmt === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const blob = b64ToBlob(item.report_b64, mime);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const base = (item.filename || 'invoice').replace(/\.[^.]+$/, '');
    a.href = url;
    a.download = `${base}_fraud_report.${fmt}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadIndividual = async (item) => {
    try {
      const fd = new FormData();
      fd.append('fmt', reportFmt);
      fd.append('analysis_json', JSON.stringify({ filename: item.filename, result: item.result }));
      const resp = await api.post('/generate-report', fd, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      const base = (item.filename || 'invoice').replace(/\.[^.]+$/, '');
      a.href = url;
      a.download = `${base}_fraud_report.${reportFmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err);
      alert('Report download failed: ' + (err?.message || 'Unknown'));
    }
  };

  const exportCsv = () => {
    if (!results || results.length === 0) return;
    const header = ['#', 'filename', 'finding_count', 'summary', 'error'];
    const rows = results.map((item, i) => {
      const r = item.result || {};
      const findings = r.findings || [];
      return [
        i + 1,
        item.filename || '',
        findings.length,
        r.summary || '',
        r.error || item.error || '',
      ];
    });
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(',')).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_fraud_analysis_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const totalSize = useMemo(() => files.reduce((sum, f) => sum + f.size, 0), [files]);

  return (
    <div className="bulk-content">
      <div className="bulk-left">
        <div className="bulk-header">
          <h2>Bulk Invoice Analysis</h2>
          <p>Upload up to {MAX_FILES} invoices and analyze them in one batch.</p>
        </div>

        <div
          className={`bulk-dropzone ${dragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="bulk-drop-icon">⬆</div>
          <div className="bulk-drop-title">
            {files.length === 0
              ? 'Drop invoices here or click to browse'
              : `${files.length} file${files.length !== 1 ? 's' : ''} selected`}
          </div>
          <div className="bulk-drop-sub">PDF, PNG, JPG, JPEG, TIFF · max {MAX_FILES} files</div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.tiff,.bmp,.webp"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />

        {files.length > 0 && (
          <>
            <div className="bulk-file-list">
              {files.map((f, idx) => (
                <div key={`${f.name}_${idx}`} className="bulk-file-row">
                  <span className="bulk-file-icon">📄</span>
                  <span className="bulk-file-name">{f.name}</span>
                  <span className="bulk-file-size">{(f.size / 1024).toFixed(1)} KB</span>
                  <button className="bulk-file-remove" onClick={() => removeFile(idx)} disabled={loading}>✕</button>
                </div>
              ))}
            </div>
            <div className="bulk-meta-row">
              <span>{files.length} files · {(totalSize / 1024).toFixed(1)} KB total</span>
              <button className="bulk-clear" onClick={clearAll} disabled={loading}>Clear all</button>
            </div>
          </>
        )}

        <div className="bulk-options">
          <label className="bulk-checkbox">
            <input
              type="checkbox"
              checked={generateReports}
              onChange={(e) => setGenerateReports(e.target.checked)}
              disabled={loading}
            />
            <span>Generate downloadable reports for each invoice</span>
          </label>
          {generateReports && (
            <div className="bulk-fmt-toggle">
              <span>Report format:</span>
              <button
                className={`bulk-fmt-btn ${reportFmt === 'pdf' ? 'active' : ''}`}
                onClick={() => setReportFmt('pdf')}
                disabled={loading}
              >PDF</button>
              <button
                className={`bulk-fmt-btn ${reportFmt === 'docx' ? 'active' : ''}`}
                onClick={() => setReportFmt('docx')}
                disabled={loading}
              >DOCX</button>
            </div>
          )}
        </div>

        <button
          className={`bulk-run-btn ${loading ? 'loading' : ''}`}
          onClick={runBulk}
          disabled={loading || files.length === 0}
        >
          {loading ? (
            <><span className="spinner"></span>Analyzing {files.length} invoices…</>
          ) : (
            <>⚡ Run Bulk Analysis ({files.length})</>
          )}
        </button>

        {error && <div className="bulk-error">⚠ {error}</div>}
      </div>

      <div className="bulk-right">
        {summary && (
          <div className="bulk-summary-card">
            <div className="bulk-summary-head">
              <div className="bulk-summary-title">Batch summary</div>
              {results && results.length > 0 && (
                <button className="bulk-csv-btn" onClick={exportCsv}>⬇ Export CSV</button>
              )}
            </div>
            <div className="bulk-summary-grid">
              <div className="bulk-summary-item">
                <div className="bulk-summary-num">{summary.total}</div>
                <div className="bulk-summary-lbl">Total</div>
              </div>
              <div className="bulk-summary-item">
                <div className="bulk-summary-num">{summary.analyzed ?? summary.success ?? 0}</div>
                <div className="bulk-summary-lbl">Analyzed</div>
              </div>
              <div className="bulk-summary-item">
                <div className="bulk-summary-num">{summary.errors ?? summary.failed ?? 0}</div>
                <div className="bulk-summary-lbl">Errors</div>
              </div>
            </div>
          </div>
        )}

        {results && results.length > 0 && (
          <div className="bulk-results-card">
            <div className="bulk-results-title">Per-invoice results</div>
            <div className="bulk-table-wrap">
              <table className="bulk-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Filename</th>
                    <th>Findings</th>
                    <th>Summary</th>
                    <th>Report</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((item, i) => {
                    const r = item.result || {};
                    const failed = !!r.error || !!item.error;
                    const findings = r.findings || [];
                    const summaryText = r.summary || '';

                    return (
                      <tr key={i} className={failed ? 'bulk-row-failed' : ''}>
                        <td>{i + 1}</td>
                        <td className="bulk-cell-filename">{item.filename}</td>
                        <td>
                          {failed ? (
                            <span className="bulk-error-msg">{r.error || item.error}</span>
                          ) : (
                            <span className="bulk-flag-chip">{findings.length}</span>
                          )}
                        </td>
                        <td className="bulk-cell-summary" title={summaryText}>
                          {failed ? '' : (summaryText.length > 120 ? summaryText.slice(0, 117) + '…' : summaryText)}
                        </td>
                        <td>
                          {!failed && (
                            item.report_b64 ? (
                              <button className="bulk-dl-btn" onClick={() => downloadEmbedded(item)}>
                                ⬇ {(item.report_fmt || reportFmt).toUpperCase()}
                              </button>
                            ) : (
                              <button className="bulk-dl-btn" onClick={() => downloadIndividual(item)}>
                                ⬇ {reportFmt.toUpperCase()}
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!results && !loading && (
          <div className="bulk-empty">
            <div className="bulk-empty-icon">⬡</div>
            <div className="bulk-empty-title">No batch run yet</div>
            <div className="bulk-empty-sub">Upload invoices and click "Run Bulk Analysis" to process them all at once.</div>
          </div>
        )}
      </div>
    </div>
  );
}
